import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import lockdownToast from '../utils/lockdownToast';

/**
 * Emergency-lockdown state + handler, shared by Home (desktop) and MobileHome.
 * Lifted verbatim from Home so behavior is identical; both screens now use this
 * single source instead of duplicating the logic.
 *
 * Polls /api/feenics/emergency/status when hasAcs is true, and POSTs to the
 * lockdown / cancel endpoints on toggle.
 */
export default function useLockdown({ hasAcs, tenantVersion }) {
  const [isLockedDown, setIsLockedDown] = useState(false);
  const [showLockdownConfirm, setShowLockdownConfirm] = useState(false);
  const [lockdownLoading, setLockdownLoading] = useState(false);

  useEffect(() => {
    const checkLockdownStatus = async () => {
      try {
        const response = await fetch('/api/feenics/emergency/status', { credentials: 'include' });
        const data = await response.json();
        setIsLockedDown(data.active || false);
      } catch (error) {
        console.error('Failed to check lockdown status:', error);
      }
    };
    if (hasAcs) checkLockdownStatus();
  }, [tenantVersion, hasAcs]);

  const handleLockdown = async () => {
    setLockdownLoading(true);
    try {
      const endpoint = isLockedDown
        ? '/api/feenics/emergency/cancel'
        : '/api/feenics/emergency/lockdown';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        if (isLockedDown) lockdownToast.released();
        else lockdownToast.engaged();
        setIsLockedDown(!isLockedDown);
      } else {
        toast.error(data.error || 'Lockdown failed');
      }
    } catch (error) {
      lockdownToast.error('Failed to execute lockdown');
    } finally {
      setLockdownLoading(false);
      setShowLockdownConfirm(false);
    }
  };

  return { isLockedDown, showLockdownConfirm, setShowLockdownConfirm, lockdownLoading, handleLockdown };
}
